/// <reference path="../types/qrcode.d.ts" />
import { requireAuth } from './auth.js';
requireAuth();
import { applyEffectsVolume, createMixedAudioContext } from './audioMix.js';
import { playEmpBlast, styleEmpBlastOverlay } from './effects/empBlast.js';
import { SnapshotAssembler, installVisibilityReporter } from './snapshotReceiver.js';
import type { SnapshotDelta } from '../shared/snapshotDelta.js';
import { io } from 'socket.io-client';
import QRCode from 'qrcode';
import { EVENTS } from '../shared/events.js';
import { createVoiceLines } from './effects/voiceLines.js';
import { getRecoveredLogs, loadLogCatalog, onLogsChanged } from './narrativeLogs.js';
import type { CollisionImpactPayload, CreateRoomPayload, GameSnapshot, HostEventLogEntryPayload, HostSessionPayload, KickPlayerPayload, LoadSavedGamePayload, PowerFailurePayload, PlayerSummary, ResourceNodeState, RoomState, SavedGameState, SonarPulsePayload, WeaponsImpactPayload } from '../shared/types.js';
import { renderMetricChart } from './metricChart.js';
import { triggerSonarPulseEffects } from './effects/sonarPulseEffects.js';
import { playCollisionImpactSound as playSharedCollisionImpactSound } from './effects/collisionImpactSound.js';
import { createHullLeakOverlay, type HullLeakOverlay } from './effects/hullLeakOverlay.js';
import { countHullBreaches, leakSprayCount } from '../shared/submarineLayout.js';
import {
  initializeSubmarineAmbience,
  notifyTerrainContact,
  updateSubmarineAmbience
} from './effects/submarineAmbientAudio.js';
import { HostCockpitView } from './rendering/HostCockpitView.js';
import { DEFAULT_GRAPHICS_QUALITY, normalizeGraphicsQuality, type GraphicsQuality } from './rendering/quality.js';
import {
  DEFAULT_RENDER_TUNING,
  RENDER_TUNING_STORAGE_KEY,
  normalizeRenderTuning,
  type RenderTuning
} from './rendering/renderTuning.js';
import {
  loadRenderTuningWithFallback,
  saveRenderTuningToLocalStorage
} from './rendering/renderTuningPersistence.js';
import { setupRenderTuningSync } from './rendering/renderTuningSync.js';
import {
  ADMIN_PREVIEW_ROOM_STATE_STORAGE_KEY,
  ADMIN_PREVIEW_SNAPSHOT_STORAGE_KEY,
  stringifyAdminPreviewRoomState,
  stringifyAdminPreviewSnapshot
} from './adminPreviewStorage.js';
import { getMapByMissionId, loadCustomMapDefinition } from './rendering/maps/index.js';
import { safeParseJson } from './utils/json.js';
import { getSourceGlbTileGridOffset } from '../shared/maps/tiledGlb.js';
import { getChapterStatus, type CampaignDefinition } from '../shared/campaign.js';
import type { SourceGlbTileDefinition } from '../shared/maps/types.js';
import { simulationToMapPoint, submarineToMapPoint } from '../shared/spatial.js';
import { BallastLevelTracker } from '../shared/ballastTracker.js';

const hostBallastTracker = new BallastLevelTracker();

const socket = io({ transports: ['websocket'] });
const hostCanvas = document.querySelector<HTMLElement>('#phaserHostCanvas');
const graphicsQualitySelect = document.querySelector<HTMLSelectElement>('#graphicsQualitySelect');
const runtimeGraphicsQualitySelect = document.querySelector<HTMLSelectElement>('#runtimeGraphicsQualitySelect');

const hostEntryScreen = document.querySelector<HTMLElement>('#hostEntryScreen');
const hostSetupScreen = document.querySelector<HTMLElement>('#hostSetupScreen');
const hostOpsScreen = document.querySelector<HTMLElement>('#hostOpsScreen');
const hostJoinAsPlayerLink = document.querySelector<HTMLAnchorElement>('#hostJoinAsPlayerLink');
const hostJoinQr = document.querySelector<HTMLCanvasElement>('#hostJoinQr');
const hostJoinQrCaption = document.querySelector<HTMLElement>('#hostJoinQrCaption');
const roomCodeElement = document.querySelector<HTMLSpanElement>('#roomCode');
const hostStatusElement = document.querySelector<HTMLParagraphElement>('#hostStatus');
const hostReadyNote = document.querySelector<HTMLParagraphElement>('#hostReadyNote');
const hostEntryHeading = document.querySelector<HTMLHeadingElement>('#hostEntryHeading');
const entryButtonGroup = document.querySelector<HTMLElement>('#entryButtonGroup');
const entryReadyActions = document.querySelector<HTMLElement>('#entryReadyActions');
const entryCastButton = document.querySelector<HTMLButtonElement>('#entryCastButton');
const entryJoinPlayerButton = document.querySelector<HTMLButtonElement>('#entryJoinPlayerButton');
const playersElement = document.querySelector<HTMLUListElement>('#players');
const lobbyPlayersElement = document.querySelector<HTMLUListElement>('#lobbyPlayers');
const missionConfigPopup = document.querySelector<HTMLElement>('#missionConfigPopup');
const openMissionConfigButton = document.querySelector<HTMLButtonElement>('#openMissionConfigButton');
const closeMissionConfigButton = document.querySelector<HTMLButtonElement>('#closeMissionConfigButton');
const startButton = document.querySelector<HTMLButtonElement>('#startButton');
const restartButton = document.querySelector<HTMLButtonElement>('#restartButton');
const stopGameButton = document.querySelector<HTMLButtonElement>('#stopGameButton');
const closeGameButton = document.querySelector<HTMLButtonElement>('#closeGameButton');
const closeRoomButton = document.querySelector<HTMLButtonElement>('#closeRoomButton');
const joinAsPlayerRuntimeButton = document.querySelector<HTMLButtonElement>('#joinAsPlayerRuntimeButton');
const joinAsCastRuntimeButton = document.querySelector<HTMLButtonElement>('#joinAsCastRuntimeButton');
const createRoomButton = document.querySelector<HTMLButtonElement>('#createRoomButton');
const missionSelect = document.querySelector<HTMLSelectElement>('#missionSelect');
const startChapterSelect = document.querySelector<HTMLSelectElement>('#startChapterSelect');
const hostMissionSelectControl = document.querySelector<HTMLElement>('#hostMissionSelectControl');
const mapUploadInput = document.querySelector<HTMLInputElement>('#mapUploadInput');
const mapUploadStatus = document.querySelector<HTMLElement>('#mapUploadStatus');
const saveGameButton = document.querySelector<HTMLButtonElement>('#saveGameButton');
const loadGameButton = document.querySelector<HTMLButtonElement>('#loadGameButton');
const loadGameInput = document.querySelector<HTMLInputElement>('#loadGameInput');
const hostSaveLoadStatus = document.querySelector<HTMLElement>('#hostSaveLoadStatus');
const difficultySelect = document.querySelector<HTMLSelectElement>('#difficultySelect');
const hostStageViewport = document.querySelector<HTMLElement>('#hostStageViewport');
const hostEventLog = document.querySelector<HTMLUListElement>('#hostEventLog');
const hullValue = document.querySelector<HTMLElement>('#hullValue');
const powerValue = document.querySelector<HTMLElement>('#powerValue');
const fuelValue = document.querySelector<HTMLElement>('#fuelValue');
const oxygenValue = document.querySelector<HTMLElement>('#oxygenValue');
const outsideTemperatureValue = document.querySelector<HTMLElement>('#outsideTemperatureValue');
const insideTemperatureValue = document.querySelector<HTMLElement>('#insideTemperatureValue');
const temperatureRateValue = document.querySelector<HTMLElement>('#temperatureRateValue');
const depthValue = document.querySelector<HTMLElement>('#depthValue');
const positionValue = document.querySelector<HTMLElement>('#positionValue');
const forwardVelocityValue = document.querySelector<HTMLElement>('#forwardVelocityValue');
const horizontalVelocityValue = document.querySelector<HTMLElement>('#horizontalVelocityValue');
const verticalVelocityValue = document.querySelector<HTMLElement>('#verticalVelocityValue');
const alertValue = document.querySelector<HTMLElement>('#alertValue');
const phaseValue = document.querySelector<HTMLElement>('#phaseValue');
const lastInput = document.querySelector<HTMLElement>('#lastInput');
const hostOverloadBanner = document.querySelector<HTMLElement>('#hostOverloadBanner');
const hostEmpFlashOverlay = document.createElement('div');
styleEmpBlastOverlay(hostEmpFlashOverlay);
document.body.appendChild(hostEmpFlashOverlay);
const hostMissionBanner = document.querySelector<HTMLElement>('#hostMissionBanner');
const hostMissionBannerText = document.querySelector<HTMLElement>('#hostMissionBannerText');
let missionBannerTimeout: ReturnType<typeof setTimeout> | null = null;
const hostEndGameSummary = document.querySelector<HTMLElement>('#hostEndGameSummary');
const hostEndGameReason = document.querySelector<HTMLElement>('#hostEndGameReason');
const hostEndGameDuration = document.querySelector<HTMLElement>('#hostEndGameDuration');
const hostEndGameDistance = document.querySelector<HTMLElement>('#hostEndGameDistance');
const hostEndGameResources = document.querySelector<HTMLElement>('#hostEndGameResources');
const hostEndGameSonar = document.querySelector<HTMLElement>('#hostEndGameSonar');
const hostEndGameChart = document.querySelector<HTMLCanvasElement>('#hostEndGameChart');
const hostEndGameRestartButton = document.querySelector<HTMLButtonElement>('#hostEndGameRestartButton');
const hostReconnectBanner = document.querySelector<HTMLDivElement>('#hostReconnectBanner');

const createKickPlayerButton = (playerId: string): HTMLButtonElement => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'kick-player-button';
  button.textContent = 'Kick';
  button.addEventListener('click', () => {
    if (!roomCode) {
      return;
    }

    socket.emit(EVENTS.kickPlayer, {
      code: roomCode,
      playerId
    } satisfies KickPlayerPayload);
  });

  return button;
};

const appendKickablePlayerItem = (list: HTMLUListElement, player: PlayerSummary): void => {
  const item = document.createElement('li');
  const details = document.createElement('span');
  const reconnectCopy = player.connected
    ? 'connected'
    : `reconnecting ${Math.ceil((player.expiresInMs ?? 0) / 1000)}s`;

  details.textContent = `${player.name} | ${player.role ?? 'unassigned'} | ${reconnectCopy}`;
  item.append(details, createKickPlayerButton(player.id));
  list.append(item);
};

const HOST_STORAGE_KEY = 'abyss-link-host-session';
const HOST_GRAPHICS_QUALITY_KEY = 'abyss-link-host-graphics-quality';


type HostScreen = 'entry' | 'setup' | 'ops';

let hostGraphicsQuality: GraphicsQuality = normalizeGraphicsQuality(
  window.localStorage.getItem(HOST_GRAPHICS_QUALITY_KEY) ?? DEFAULT_GRAPHICS_QUALITY
);
let hostRenderTuning: RenderTuning = DEFAULT_RENDER_TUNING;

let hostScreen: HostScreen = 'entry';
let lastLoggedInput = '';
let lastRenderedJoinUrl = '';
let lastHostEmpEventTick: number | null = null;

const triggerHostEmpEffects = (): void => {
  playEmpBlast(hostEmpFlashOverlay);
  playHostEmpSound();
};

const playHostEmpSound = (): void => {
  const ContextCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!ContextCtor) {
    return;
  }
  const context = createMixedAudioContext() as AudioContext;
  const now = context.currentTime;
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
  gain.connect(context.destination);

  const oscillator = context.createOscillator();
  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(440, now);
  oscillator.frequency.linearRampToValueAtTime(980, now + 0.16);
  oscillator.connect(gain);
  oscillator.start(now);
  oscillator.stop(now + 0.18);
};

const playHostPowerDownSound = (): void => {
  const ContextCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!ContextCtor) {
    return;
  }
  const context = createMixedAudioContext() as AudioContext;
  const now = context.currentTime;

  const hum = context.createOscillator();
  hum.type = 'sawtooth';
  hum.frequency.setValueAtTime(180, now);
  hum.frequency.exponentialRampToValueAtTime(28, now + 2.4);

  const humGain = context.createGain();
  humGain.gain.setValueAtTime(0.18, now);
  humGain.gain.linearRampToValueAtTime(0.0, now + 2.6);
  hum.connect(humGain);
  humGain.connect(context.destination);
  hum.start(now);
  hum.stop(now + 2.6);

  const noiseBuffer = context.createBuffer(1, context.sampleRate * 0.25, context.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1);
  }
  const crackle = context.createBufferSource();
  crackle.buffer = noiseBuffer;
  const crackleFilter = context.createBiquadFilter();
  crackleFilter.type = 'bandpass';
  crackleFilter.frequency.value = 3200;
  crackleFilter.Q.value = 2;
  const crackleGain = context.createGain();
  crackleGain.gain.setValueAtTime(0.22, now);
  crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  crackle.connect(crackleFilter);
  crackleFilter.connect(crackleGain);
  crackleGain.connect(context.destination);
  crackle.start(now);
  crackle.stop(now + 0.25);
};

// Persistent AudioContext for host one-shot impact sounds. Reused across
// collisions — creating a fresh context per hit leaks contexts and browsers
// cap how many can exist.
let hostImpactAudioContext: AudioContext | null = null;

const getHostImpactAudioContext = (): AudioContext | null => {
  const ContextCtor = window.AudioContext
    ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!ContextCtor) {
    return null;
  }
  if (!hostImpactAudioContext) {
    hostImpactAudioContext = createMixedAudioContext() as AudioContext;
  }
  if (hostImpactAudioContext.state === 'suspended') {
    void hostImpactAudioContext.resume();
  }
  return hostImpactAudioContext;
};

const playHostCollisionImpactSound = (strength: number): void => {
  const context = getHostImpactAudioContext();
  if (!context) {
    return;
  }
  playSharedCollisionImpactSound(context, strength);
};

if (graphicsQualitySelect) {
  graphicsQualitySelect.value = hostGraphicsQuality;
}

if (runtimeGraphicsQualitySelect) {
  runtimeGraphicsQualitySelect.value = hostGraphicsQuality;
}

if (hostStageViewport) {
  hostStageViewport.hidden = true;
}

const createHostView = (): HostCockpitView | null => {
  if (hostViewInitFailed) {
    return null;
  }

  if (!hostCanvas) {
    return null;
  }

  try {
    return new HostCockpitView(hostCanvas, hostGraphicsQuality, hostRenderTuning);
  } catch (error) {
    hostViewInitFailed = true;
    console.error('Failed to initialize host 3D view:', error);
    if (hostStatusElement) {
      hostStatusElement.textContent = 'Connected, but 3D viewport failed to initialize. Check browser WebGL support.';
    }
    return null;
  }
};

let hostView: HostCockpitView | null = null;

let roomCode = '';
let hostReconnectToken = '';
let hostAutoRestorePending = false;
let createFreshRoomPending = false;
let hostViewInitFailed = false;
let lastRoomState: RoomState | null = null;
let lastSnapshot: GameSnapshot | null = null;
// The server omits world.resourceNodes on ticks where it's unchanged; cache the
// last received list and refill so rendering/save logic always sees it.
let cachedResourceNodes: GameSnapshot['world']['resourceNodes'] = [];
const playedHullWarnings = new Set<number>();
let pendingUploadedCustomMapId: string | null = null;
let pendingUploadedCustomMapName: string | null = null;
let mapUploadInProgress = false;

type StoredHostSession = {
  code: string;
  reconnectToken: string;
};

const loadHostSession = (): StoredHostSession | null => {
  try {
    const raw = window.localStorage.getItem(HOST_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = safeParseJson<StoredHostSession>(raw);
    return parsed && typeof parsed.code === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

const persistHostSession = (): void => {
  if (!roomCode || !hostReconnectToken) {
    return;
  }

  window.localStorage.setItem(
    HOST_STORAGE_KEY,
    JSON.stringify({ code: roomCode, reconnectToken: hostReconnectToken } satisfies StoredHostSession)
  );
};

const clearHostSession = (): void => {
  window.localStorage.removeItem(HOST_STORAGE_KEY);
};

const formatMissionLabel = (rawLabel: string): string => {
  const trimmed = rawLabel.trim();
  if (!trimmed) {
    return trimmed;
  }

  const segments = trimmed.split(/[/\\]+/).filter(Boolean);
  if (segments.length > 1) {
    return segments.slice(0, -1).join('/');
  }

  return segments[0].replace(/\.[^.]+$/, '');
};

const CAMPAIGN_OPTION_PREFIX = 'campaign:';
/** Campaign id -> the map of its first playable chapter (the lobby config carries a map id too, so a leader can preload it). */
const campaignFirstMap = new Map<string, string>();
/** Dev "Start at chapter": campaign id -> its chapters (index in the campaign, title). Filled with the campaign options. */
const campaignChapters = new Map<string, Array<{ index: number; title: string }>>();
/** The chapter picked in the dev selector, sent once with the next lobby config (and with Launch); cleared after launch so a retry or the next chapter is not rewound. */
let pendingStartChapter: number | undefined;

const refreshStartChapterOptions = (): void => {
  if (!startChapterSelect) return;
  const selected = missionSelect?.value ?? '';
  const campaignId = selected.startsWith(CAMPAIGN_OPTION_PREFIX) ? selected.slice(CAMPAIGN_OPTION_PREFIX.length) : '';
  const chapters = campaignChapters.get(campaignId) ?? [];
  startChapterSelect.replaceChildren();
  const chapterGroup = document.createElement('optgroup');
  chapterGroup.label = 'Campaign chapters (in play order)';
  for (const chapter of chapters) {
    const option = document.createElement('option');
    option.value = `ch:${chapter.index}`;
    option.textContent = `${chapter.index + 1}. ${chapter.title}`;
    chapterGroup.appendChild(option);
  }
  if (chapters.length > 0) startChapterSelect.appendChild(chapterGroup);
  const mapGroup = document.createElement('optgroup');
  mapGroup.label = 'Standalone maps (not part of the campaign)';
  for (const option of Array.from(missionSelect?.options ?? [])) {
    if (option.value.startsWith(CAMPAIGN_OPTION_PREFIX)) continue;
    const copy = document.createElement('option');
    copy.value = `map:${option.value}`;
    copy.textContent = option.textContent;
    mapGroup.appendChild(copy);
  }
  startChapterSelect.appendChild(mapGroup);
  startChapterSelect.value = campaignId ? 'ch:0' : `map:${selected}`;
};

startChapterSelect?.addEventListener('change', () => {
  const value = startChapterSelect.value;
  if (value.startsWith('map:') && missionSelect) {
    // A standalone map: switch the mission dropdown to it.
    pendingStartChapter = undefined;
    missionSelect.value = value.slice(4);
    missionSelect.dispatchEvent(new Event('change'));
    return;
  }
  pendingStartChapter = Number.parseInt(value.slice(3), 10);
  if (!roomCode) return;
  const { mission, difficulty, campaignId } = getSelectedMissionAndDifficulty();
  socket.emit(EVENTS.setLobbyConfig, { code: roomCode, mission, difficulty, campaignId, startChapter: pendingStartChapter });
});

const loadCustomMissionOptions = async (): Promise<void> => {
  if (!missionSelect) {
    return;
  }

  try {
    const mapsResponse = await fetch('/api/maps/list').catch(() => null);

    const maps = mapsResponse?.ok
      ? (await mapsResponse.json()) as Array<{ id: string; name: string; lastModified?: number; sourceGlbFile?: string }>
      : [];

    if (maps.length === 0) {
      return;
    }

    missionSelect.innerHTML = '';

    // Campaigns first: choosing one plays its chapters in order (see campaigns/*.json).
    const campaignsResponse = await fetch('/api/campaigns').catch(() => null);
    const campaigns = campaignsResponse?.ok ? (await campaignsResponse.json()) as CampaignDefinition[] : [];
    campaignFirstMap.clear();
    // Maps that are a campaign chapter are reached through the campaign (numbered), never listed again as loose maps:
    // their raw map names (Chapter3, Chapter_Sewer ...) do not match the chapter numbers and made the list confusing.
    const chapterMapIds = new Set<string>();
    for (const campaign of campaigns) {
      for (const chapter of campaign.chapters) if (chapter.mapId) chapterMapIds.add(chapter.mapId);
    }
    for (const campaign of campaigns) {
      const first = campaign.chapters.find((chapter) => getChapterStatus(chapter) === 'ready' && chapter.mapId);
      if (!first?.mapId) continue;
      campaignFirstMap.set(campaign.id, first.mapId);
      campaignChapters.set(campaign.id, campaign.chapters
        .map((chapter, index) => ({ chapter, index }))
        .filter(({ chapter }) => getChapterStatus(chapter) === 'ready' && chapter.mapId)
        .map(({ chapter, index }) => ({ index, title: chapter.title })));
      const option = document.createElement('option');
      option.value = `${CAMPAIGN_OPTION_PREFIX}${campaign.id}`;
      option.textContent = `Campaign: ${campaign.name}`;
      missionSelect.appendChild(option);
    }

    for (const map of maps) {
      if (!map?.id || chapterMapIds.has(map.id)) continue;
      const option = document.createElement('option');
      option.value = map.id;
      option.textContent = `Standalone map: ${map.name || formatMissionLabel(map.sourceGlbFile ?? map.id)}`;
      missionSelect.appendChild(option);
    }
    refreshStartChapterOptions();
  } catch (error) {
    console.warn('Failed to load custom mission options:', error);
  }
};

const addCustomMissionOption = (missionId: string, label: string): void => {
  if (!missionSelect) {
    return;
  }

  let option = Array.from(missionSelect.options).find((entry) => entry.value === missionId);
  if (!option) {
    option = document.createElement('option');
    option.value = missionId;
    missionSelect.appendChild(option);
  }

  option.textContent = `Custom: ${formatMissionLabel(label)}`;
};

const setMapUploadStatus = (message: string): void => {
  if (!mapUploadStatus) {
    return;
  }

  mapUploadStatus.textContent = message;
};

const startPendingUploadedMap = (): void => {
  if (!pendingUploadedCustomMapId || !roomCode || lastRoomState?.phase === 'running') {
    return;
  }

  const difficulty = difficultySelect?.value || 'normal';
  socket.emit(EVENTS.setLobbyConfig, { code: roomCode, mission: pendingUploadedCustomMapId, difficulty });
  socket.emit(EVENTS.startGame, { code: roomCode });
  appendHostEvent(`Game started with uploaded mission ${pendingUploadedCustomMapName ?? pendingUploadedCustomMapId}.`);
  pendingUploadedCustomMapId = null;
  pendingUploadedCustomMapName = null;
  setMapUploadStatus('');
};

const queuePendingUploadedMapStart = (): void => {
  if (!pendingUploadedCustomMapId) {
    return;
  }

  if (roomCode && lastRoomState?.phase === 'running') {
    createRoomAfterClosingExisting();
    return;
  }

  if (!roomCode) {
    createFreshRoom();
    return;
  }

  startPendingUploadedMap();
};

const uploadMapFile = async (file: File): Promise<{ id: string; name: string; sourceGlbFile?: string; lastModified?: number }> => {
  const url = `/api/maps/import?filename=${encodeURIComponent(file.name)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream'
    },
    body: await file.arrayBuffer()
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const payload = (await response.json()) as { message?: string };
      if (payload?.message) {
        message = payload.message;
      }
    } catch {
      // ignore
    }
    throw new Error(message || `Upload failed with status ${response.status}`);
  }

  return (await response.json()) as { id: string; name: string; sourceGlbFile?: string; lastModified?: number };
};

mapUploadInput?.addEventListener('change', async () => {
  const file = mapUploadInput?.files?.[0];
  if (!file) {
    return;
  }

  mapUploadInput.value = '';

  if (!file.name.toLowerCase().endsWith('.glb')) {
    setMapUploadStatus('Please select a .glb file.');
    return;
  }

  if (mapUploadInProgress) {
    return;
  }

  mapUploadInProgress = true;
  setMapUploadStatus(`Uploading ${file.name}...`);

  try {
    const result = await uploadMapFile(file);
    pendingUploadedCustomMapId = result.id;
    pendingUploadedCustomMapName = result.name;
    addCustomMissionOption(result.id, result.sourceGlbFile ?? result.name);
    if (missionSelect) {
      missionSelect.value = result.id;
    }
    setMapUploadStatus(`Uploaded ${result.name}. Starting game now...`);
    queuePendingUploadedMapStart();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Custom map upload failed.';
    setMapUploadStatus(message);
    appendHostEvent(`Map upload failed: ${message}`);
  } finally {
    mapUploadInProgress = false;
  }
});

saveGameButton?.addEventListener('click', async () => {
  if (!canSaveGame()) {
    setSaveLoadStatus('Save unavailable: submarine must be docked.');
    return;
  }

  const savedGame = createSavedGameState();
  if (!savedGame) {
    setSaveLoadStatus('Unable to create save file.');
    return;
  }

  try {
    const response = await fetch('/api/games/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: `Save ${roomCode || 'room'} ${new Date().toISOString()}`,
        roomCode: roomCode || null,
        mission: lastRoomState?.mission || null,
        difficulty: lastRoomState?.difficulty || null,
        savedGame
      })
    });

    if (response.ok) {
      appendHostEvent('Saved current docked game state to server.');
      setSaveLoadStatus('Saved game state to server.');
      void refreshSavedGamesList();
      return;
    }

    throw new Error(`Server save failed: ${response.status} ${response.statusText}`);
  } catch (error) {
    appendHostEvent(`Server save failed, falling back to download: ${error instanceof Error ? error.message : String(error)}`);
    downloadSavedGame(savedGame);
    appendHostEvent('Saved current docked game state locally.');
    setSaveLoadStatus('Saved game file downloaded.');
  }
});

const savedGamesSelect = document.querySelector<HTMLSelectElement>('#savedGamesSelect');
const loadServerSaveButton = document.querySelector<HTMLButtonElement>('#loadServerSaveButton');

type SavedGameListItem = {
  id: string;
  title: string;
  savedBy: string | null;
  participants: Array<{ userId: number; name: string }>;
};

/** Fill the picker with the saves stored on the server (newest first). */
const refreshSavedGamesList = async (): Promise<void> => {
  if (!savedGamesSelect || !loadServerSaveButton) return;
  try {
    const response = await fetch('/api/games/list');
    if (!response.ok) throw new Error(String(response.status));
    const saves = await response.json() as SavedGameListItem[];
    savedGamesSelect.replaceChildren();
    if (saves.length === 0) {
      savedGamesSelect.append(new Option('No saved games on the server', ''));
    }
    for (const save of saves) {
      const crew = save.participants.map((p) => p.name).join(', ');
      savedGamesSelect.append(new Option(crew ? `${save.title} (${crew})` : save.title, save.id));
    }
    loadServerSaveButton.disabled = saves.length === 0;
  } catch {
    savedGamesSelect.replaceChildren(new Option('Saved games unavailable', ''));
    loadServerSaveButton.disabled = true;
  }
};

loadServerSaveButton?.addEventListener('click', async () => {
  const id = savedGamesSelect?.value;
  if (!id) return;
  if (!roomCode) {
    setSaveLoadStatus('Create or restore a room before loading a save.');
    return;
  }
  try {
    const response = await fetch(`/api/games/${encodeURIComponent(id)}`);
    if (!response.ok) throw new Error(`Server returned ${response.status}`);
    const record = await response.json() as { title: string; payload: SavedGameState };
    if (!isSavedGameState(record.payload)) throw new Error('That save is not a valid Aegis7 save.');
    const payload: LoadSavedGamePayload = { code: roomCode, savedGame: record.payload };
    socket.emit(EVENTS.loadSavedGame, payload);
    appendHostEvent(`Loading server save ${record.title}.`);
    setSaveLoadStatus(`Loaded server save: ${record.title}`);
    setEntryCreating();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load server save.';
    setSaveLoadStatus(message);
    appendHostEvent(`Server save load failed: ${message}`);
  }
});

void refreshSavedGamesList();

loadGameButton?.addEventListener('click', () => {
  loadGameInput?.click();
});

loadGameInput?.addEventListener('change', async () => {
  const file = loadGameInput?.files?.[0];
  if (!file) {
    return;
  }

  loadGameInput.value = '';

  if (!file.name.toLowerCase().endsWith('.json')) {
    setSaveLoadStatus('Please select a .json save file.');
    return;
  }

  if (!roomCode) {
    setSaveLoadStatus('Create or restore a room before loading a save.');
    return;
  }

  try {
    const savedGame = await loadSavedGameFile(file);
    const payload: LoadSavedGamePayload = { code: roomCode, savedGame };
    socket.emit(EVENTS.loadSavedGame, payload);
    appendHostEvent(`Loading save file ${file.name}.`);
    setSaveLoadStatus(`Loaded save file: ${file.name}`);
    setEntryCreating();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load save file.';
    setSaveLoadStatus(message);
    appendHostEvent(`Save load failed: ${message}`);
  }
});

const openJoinTargetInNewTab = (joinUrl: string): void => {
  const popup = window.open(joinUrl, '_blank', 'noopener,noreferrer');
  if (popup) {
    popup.opener = null;
  }
};

joinAsPlayerRuntimeButton?.addEventListener('click', () => {
  if (!roomCode) {
    return;
  }

  openJoinTargetInNewTab(resolveControllerJoinUrl());
});

joinAsCastRuntimeButton?.addEventListener('click', () => {
  if (!roomCode) {
    return;
  }

  openJoinTargetInNewTab(resolveCastJoinUrl());
});

const setEntryRoomReady = (): void => {
  if (hostEntryHeading) hostEntryHeading.textContent = 'Ready to begin';
  if (hostStatusElement) hostStatusElement.textContent = 'Lobby is open. Crew can join now.';
  if (hostReadyNote) hostReadyNote.style.display = '';
  if (entryButtonGroup) entryButtonGroup.style.display = '';
  if (entryReadyActions) entryReadyActions.style.display = 'none';
};

const setEntryCreating = (): void => {
  if (hostEntryHeading) hostEntryHeading.textContent = 'Creating Game…';
  if (hostReadyNote) hostReadyNote.style.display = 'none';
  if (entryButtonGroup) entryButtonGroup.style.display = 'none';
  if (entryReadyActions) entryReadyActions.style.display = 'none';
};

const setEntryReady = (code: string): void => {
  if (hostEntryHeading) hostEntryHeading.textContent = 'Ready';
  if (hostStatusElement) hostStatusElement.textContent = 'Game is ready. Cast the view to your screen.';
  if (hostReadyNote) {
    hostReadyNote.textContent = 'Leave this window open.';
    hostReadyNote.style.display = '';
  }
  if (entryButtonGroup) entryButtonGroup.style.display = 'none';
  if (entryCastButton) entryCastButton.onclick = () => {
    openJoinTargetInNewTab(resolveCastJoinUrl());
  };
  if (entryJoinPlayerButton) entryJoinPlayerButton.onclick = () => {
    openJoinTargetInNewTab(resolveControllerJoinUrl());
  };
  if (entryReadyActions) entryReadyActions.style.display = '';
};

const createFreshRoom = (): void => {
  hostReconnectToken = '';
  clearHostSession();
  socket.emit(EVENTS.createRoom, {} satisfies CreateRoomPayload);
  if (hostStatusElement) {
    hostStatusElement.textContent = 'Creating room...';
  }
  // Reload render tuning from localStorage after creating a new room
  void loadRenderTuning();
};

const createRoomAfterClosingExisting = (): void => {
  if (roomCode) {
    createFreshRoomPending = true;
    socket.emit(EVENTS.closeRoom, { code: roomCode });
    if (hostStatusElement) {
      hostStatusElement.textContent = 'Closing previous room...';
    }
    return;
  }

  createFreshRoom();
};

const syncGraphicsQualitySelects = (): void => {
  if (graphicsQualitySelect) {
    graphicsQualitySelect.value = hostGraphicsQuality;
  }
  if (runtimeGraphicsQualitySelect) {
    runtimeGraphicsQualitySelect.value = hostGraphicsQuality;
  }
};

const setHostScreen = (screen: HostScreen): void => {
  hostScreen = screen;
  if (hostEntryScreen) hostEntryScreen.hidden = screen !== 'entry';
  if (hostSetupScreen) hostSetupScreen.hidden = screen !== 'setup';
  if (hostOpsScreen) hostOpsScreen.hidden = screen !== 'ops';
  if (hostStageViewport) hostStageViewport.hidden = screen !== 'ops';

  if (screen === 'ops') {
    window.requestAnimationFrame(() => {
      if (!hostView) {
        hostView = createHostView();
        if (!hostView) {
          return;
        }
      }

      hostView.syncViewportSize();
      if (lastSnapshot) {
        hostView.setSnapshot(lastSnapshot);
      }
      if (lastRoomState) {
        hostView.setRoomState(lastRoomState);
      }
    });
  } else if (screen === 'entry') {
    hostView?.dispose();
    hostView = null;
  }
};

const setSaveLoadStatus = (message: string): void => {
  if (!hostSaveLoadStatus) {
    return;
  }
  hostSaveLoadStatus.textContent = message;
};

const canSaveGame = (): boolean => Boolean(lastSnapshot?.submarine?.docked && roomCode && !hostViewInitFailed);

const setSaveGameButtonState = (): void => {
  if (saveGameButton) {
    saveGameButton.disabled = !canSaveGame();
  }
};

const getCurrentMapTile = (): { tileX: number; tileZ: number } | null => {
  if (!lastRoomState || !lastSnapshot) {
    return null;
  }

  const currentMap = getMapByMissionId(lastRoomState.mission);
  if (!currentMap?.sourceGlbTiles || !currentMap.sourceGlbTileSizeMeters) {
    return null;
  }

  const mapPoint = submarineToMapPoint(lastSnapshot.submarine);
  const gridOffset = getSourceGlbTileGridOffset(currentMap.sourceGlbTiles, currentMap.sourceGlbTileSizeMeters, currentMap.sourceGlbGridBounds);
  const localX = mapPoint.x + gridOffset.x;
  const localZ = mapPoint.z + gridOffset.z;

  return {
    tileX: Math.floor(localX / currentMap.sourceGlbTileSizeMeters.X),
    tileZ: Math.floor(localZ / currentMap.sourceGlbTileSizeMeters.Z)
  };
};

const getDockingPortLabel = (): string | null => {
  if (!lastRoomState || !lastSnapshot) {
    return null;
  }

  const currentMap = getMapByMissionId(lastRoomState.mission);
  if (!currentMap?.glbModelMarkers?.length) {
    return null;
  }

  const submarinePoint = submarineToMapPoint(lastSnapshot.submarine);
  let nearest: { markerLabel: string | null; distance: number } = { markerLabel: null, distance: Infinity };

  for (const marker of currentMap.glbModelMarkers) {
    if (marker.model.toLowerCase() !== 'docking_port') {
      continue;
    }

    const dx = marker.mapX - submarinePoint.x;
    const dz = marker.mapZ - submarinePoint.z;
    const dy = -marker.depth - (-lastSnapshot.submarine.depth);
    const distance = Math.hypot(dx, dz, dy);
    if (distance < nearest.distance) {
      nearest = { markerLabel: marker.label ?? marker.model ?? null, distance };
    }
  }

  return nearest.markerLabel;
};

const getChangedResourceNodes = (): ResourceNodeState[] => {
  if (!lastRoomState || !lastSnapshot) {
    return [];
  }

  const currentNodes = lastSnapshot.world.resourceNodes;
  const currentMap = getMapByMissionId(lastRoomState.mission);
  const originalNodes = currentMap?.glbResourceNodes ?? [];
  const originalMap = new Map(
    originalNodes.map((node) => [`${node.mapX}|${node.mapZ}|${node.depth}|${node.resourceType}`, node.resourceValue])
  );

  return currentNodes.filter((node) => {
    const currentMapPoint = simulationToMapPoint({ x: node.simulationX, y: node.simulationZ });
    const originalValue = originalMap.get(`${currentMapPoint.x}|${currentMapPoint.z}|${node.depth}|${node.resourceType}`);
    return originalValue === undefined ? node.resourceValue !== 0 : node.resourceValue !== originalValue;
  });
};

const createSavedGameState = (): SavedGameState | null => {
  if (!lastRoomState || !lastSnapshot) {
    return null;
  }

  if (!lastSnapshot.submarine.docked) {
    return null;
  }

  return {
    version: 1,
    mission: lastRoomState.mission,
    missionLabel: formatMissionLabel(lastRoomState.mission),
    savedAt: new Date().toISOString(),
    dockingPortLabel: getDockingPortLabel(),
    mapTile: getCurrentMapTile(),
    submarine: {
      simulationX: lastSnapshot.submarine.simulationX,
      simulationZ: lastSnapshot.submarine.simulationZ,
      depth: lastSnapshot.submarine.depth,
      yaw: lastSnapshot.submarine.yaw,
      fuel: lastSnapshot.submarine.fuel,
      currentFuelType: lastSnapshot.submarine.currentFuelType,
      fuelCapacity: lastSnapshot.submarine.fuelCapacity,
      docked: lastSnapshot.submarine.docked,
      outsideTemperature: lastSnapshot.submarine.outsideTemperature,
      insideTemperature: lastSnapshot.submarine.insideTemperature,
      temperatureRate: lastSnapshot.submarine.temperatureRate
    },
    cargo: lastRoomState.cargo,
    craftQueue: lastRoomState.craftQueue,
    autoCraft: lastRoomState.autoCraft,
    wares: lastRoomState.wares,
    ownedUpgrades: lastRoomState.ownedUpgrades,
    resourceNodes: getChangedResourceNodes(),
    metricHistory: lastRoomState.metricHistory ?? []
  };
};

const downloadSavedGame = (savedGame: SavedGameState): void => {
  const blob = new Blob([JSON.stringify(savedGame, null, 2)], { type: 'application/json' });
  const fileName = `aegis7_save_${roomCode || 'room'}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const isSavedGameState = (value: unknown): value is SavedGameState => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as SavedGameState;
  return (
    payload.version === 1 &&
    typeof payload.mission === 'string' &&
    typeof payload.savedAt === 'string' &&
    (typeof payload.dockingPortLabel === 'string' || payload.dockingPortLabel === null) &&
    (payload.mapTile === null || (
      typeof payload.mapTile === 'object' &&
      payload.mapTile !== null &&
      typeof payload.mapTile.tileX === 'number' &&
      typeof payload.mapTile.tileZ === 'number'
    )) &&
    typeof payload.cargo === 'object' && payload.cargo !== null &&
    Array.isArray(payload.craftQueue) &&
    Array.isArray(payload.resourceNodes) &&
    payload.submarine !== undefined &&
    typeof payload.submarine.simulationX === 'number' &&
    typeof payload.submarine.simulationZ === 'number' &&
    typeof payload.submarine.depth === 'number' &&
    typeof payload.submarine.yaw === 'number' &&
    typeof payload.submarine.fuel === 'number' &&
    typeof payload.submarine.docked === 'boolean'
  );
};

const loadSavedGameFile = async (file: File): Promise<SavedGameState> => {
  const text = await file.text();
  const parsed = safeParseJson<unknown>(text);
  if (!parsed || !isSavedGameState(parsed)) {
    throw new Error('Invalid save file format.');
  }
  return parsed;
};

const appendHostEvent = (message: string): void => {
  if (!hostEventLog || !message) {
    return;
  }

  const stamp = new Date().toLocaleTimeString();
  const item = document.createElement('li');
  item.textContent = `${stamp} - ${message}`;
  hostEventLog.prepend(item);

  while (hostEventLog.childElementCount > 10) {
    hostEventLog.removeChild(hostEventLog.lastElementChild!);
  }

  if (roomCode) {
    const payload: HostEventLogEntryPayload = { code: roomCode, message, stamp };
    socket.emit(EVENTS.hostEventLogEntry, payload);
  }
};

const formatDuration = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
};

const resolveControllerJoinUrl = (): string => {
  const url = new URL('/controller', window.location.origin);
  if (roomCode) {
    url.searchParams.set('code', roomCode);
  }
  return url.toString();
};

const resolveCastJoinUrl = (): string => {
  const url = new URL('/cast', window.location.origin);
  if (roomCode) {
    url.searchParams.set('code', roomCode);
  }
  return url.toString();
};

const updateJoinLinkAndQr = (): void => {
  const joinUrl = resolveControllerJoinUrl();

  if (hostJoinAsPlayerLink) {
    hostJoinAsPlayerLink.href = joinUrl;
  }

  if (hostJoinQrCaption) {
    hostJoinQrCaption.textContent = roomCode
      ? `Scan to open controller with room code ${roomCode}.`
      : 'Scan to open the controller page on a phone.';
  }

  if (!hostJoinQr || joinUrl === lastRenderedJoinUrl) {
    return;
  }

  lastRenderedJoinUrl = joinUrl;
  void QRCode.toCanvas(hostJoinQr, joinUrl, {
    width: 120,
    margin: 1,
    color: {
      dark: '#111111',
      light: '#ffffff'
    }
  });
};

const setGraphicsQuality = (nextQuality: string): void => {
  const normalized = normalizeGraphicsQuality(nextQuality);
  if (normalized === hostGraphicsQuality) {
    syncGraphicsQualitySelects();
    return;
  }

  hostGraphicsQuality = normalized;
  window.localStorage.setItem(HOST_GRAPHICS_QUALITY_KEY, hostGraphicsQuality);
  syncGraphicsQualitySelects();
  resetHostView();
};

const applyRenderTuning = (tuning: RenderTuning, persistLocal = true): void => {
  hostRenderTuning = normalizeRenderTuning(tuning);
  if (persistLocal) {
    saveRenderTuningToLocalStorage(hostRenderTuning);
  }
  hostView?.setRenderTuning(hostRenderTuning);
};

const loadRenderTuning = async (): Promise<void> => {
  const tuning = await loadRenderTuningWithFallback();
  applyRenderTuning(tuning, true);
};

const showHostBanner = (message: string): void => {
  if (!hostReconnectBanner) {
    return;
  }

  hostReconnectBanner.textContent = message;
  hostReconnectBanner.hidden = false;
  window.setTimeout(() => {
    hostReconnectBanner.hidden = true;
  }, 3200);
};

const attemptHostRestore = (): void => {
  const storedSession = loadHostSession();
  if (!storedSession) {
    // Auto-create a new room so the lobby is immediately ready
    createFreshRoom();
    return;
  }

  hostAutoRestorePending = true;
  hostReconnectToken = storedSession.reconnectToken;
  const payload: CreateRoomPayload = { reconnectToken: storedSession.reconnectToken };
  socket.emit(EVENTS.createRoom, payload);
  if (hostStatusElement) {
    hostStatusElement.textContent = `Restoring room ${storedSession.code}...`;
  }
};

const resetHostView = (): void => {
  hostView?.dispose();
  hostView = null;

  if (hostScreen !== 'ops') {
    return;
  }

  hostView = createHostView();
  if (!hostView) {
    return;
  }

  // Always apply the latest render tuning after recreating the host view
  hostView.setRenderTuning(hostRenderTuning);

  if (lastRoomState) {
    hostView.setRoomState(lastRoomState);
  }
  if (lastSnapshot) {
    hostView.setSnapshot(lastSnapshot);
  }
};

const updateMissionHud = (state: RoomState): void => {
  if (state.missionBanner) {
    const { groupTitle, stepTitle, message } = state.missionBanner;
    showMissionBanner(groupTitle && stepTitle ? `${groupTitle} — ${stepTitle}: ${message}` : message);
  }
};

const showMissionBanner = (message: string): void => {
  if (!hostMissionBanner || !hostMissionBannerText) return;
  if (missionBannerTimeout !== null) clearTimeout(missionBannerTimeout);
  hostMissionBannerText.textContent = message;
  hostMissionBanner.hidden = false;
  // Re-trigger CSS animation
  hostMissionBanner.classList.remove('host-mission-banner');
  void hostMissionBanner.offsetWidth;
  hostMissionBanner.classList.add('host-mission-banner');
  missionBannerTimeout = setTimeout(() => {
    if (hostMissionBanner) hostMissionBanner.hidden = true;
    missionBannerTimeout = null;
  }, Math.min(4000, Math.max(2600, 1600 + message.length * 40)));
};

let lastAnnouncedChapterKey = '';

const renderRoomState = async (state: RoomState, forceMapReload = false): Promise<void> => {
  lastRoomState = state;
  if (state.campaign) {
    const key = `${state.campaign.chapterId}:${state.campaign.betweenChapters}`;
    if (key !== lastAnnouncedChapterKey) {
      lastAnnouncedChapterKey = key;
      appendHostEvent(state.campaign.betweenChapters
        ? (state.campaign.campaignComplete ? 'Campaign complete.' : `Chapter complete. Next: ${state.campaign.chapterTitle}.`)
        : `Chapter ${state.campaign.chapterIndex + 1}/${state.campaign.chapterCount}: ${state.campaign.chapterTitle}.`);
    }
  } else {
    lastAnnouncedChapterKey = '';
  }
  roomCode = state.code;
  renderHostLogsRecovered();
  const currentMap = getMapByMissionId(state.mission);
  const isCustomMap = currentMap?.id === state.mission;

  if (currentMap == null || (forceMapReload && isCustomMap)) {
    await loadCustomMapDefinition(state.mission, forceMapReload);
  }

  hostView?.setRoomState(state, forceMapReload);
  window.localStorage.setItem(
    ADMIN_PREVIEW_ROOM_STATE_STORAGE_KEY,
    stringifyAdminPreviewRoomState({ mission: state.mission })
  );
  persistHostSession();

  if (hostMissionSelectControl) hostMissionSelectControl.hidden = state.phase === 'running';

  if (state.phase === 'running') {
    if (hostScreen === 'entry') {
      // Game started while on entry screen — stay and show Ready state.
      setEntryReady(state.code);
    } else {
      setHostScreen('ops');
    }
  } else {
    if (hostScreen === 'entry') {
      // Room is ready but game hasn't started — stay on entry screen.
      setEntryRoomReady();
    } else {
      setHostScreen('setup');
    }
  }

  if (roomCodeElement) {
    roomCodeElement.textContent = state.code;
  }

  setSaveGameButtonState();
  updateMissionHud(state);

  if (hostStatusElement) {
    hostStatusElement.textContent = `${state.players.length} crew connected.`;
  }

  if (playersElement) {
    playersElement.innerHTML = '';

    for (const player of state.players) {
      const item = document.createElement('li');
      const reconnectCopy = player.connected
        ? 'connected'
        : `reconnecting ${Math.ceil((player.expiresInMs ?? 0) / 1000)}s`;
      item.textContent = `${player.name} | ${player.role ?? 'unassigned'} | ${reconnectCopy}`;
      playersElement.append(item);
    }

    if (state.players.length === 0) {
      const item = document.createElement('li');
      item.textContent = 'No crew connected yet.';
      playersElement.append(item);
    } else {
      for (const player of state.players) {
        appendKickablePlayerItem(playersElement, player);
      }
    }
  }

  // Also sync the lobby crew list (visible in the setup/lobby screen)
  if (lobbyPlayersElement) {
    lobbyPlayersElement.innerHTML = '';

    if (state.players.length === 0) {
      const item = document.createElement('li');
      item.textContent = 'No crew connected yet.';
      lobbyPlayersElement.append(item);
    } else {
      for (const player of state.players) {
        appendKickablePlayerItem(lobbyPlayersElement, player);
      }
    }
  }

  if (phaseValue) {
    phaseValue.textContent = state.phase === 'running' ? 'Mission running' : 'Lobby';
  }

  updateJoinLinkAndQr();
};

const getHostDepthDisplayOffset = (): number => {
  if (!lastRoomState) return 0;
  const currentMap = getMapByMissionId(lastRoomState.mission);
  return currentMap?.missionLayout?.submarineSpawn?.startingDepthOffset ?? 0;
};

const playHullWarning = (hull: number): void => {
  // Find the lowest threshold that applies
  let lowestThreshold: number | null = null;
  for (const threshold of [75, 50, 25]) {
    if (hull < threshold) {
      lowestThreshold = threshold;
    }
  }

  // Clear all thresholds above current hull level
  for (const threshold of [75, 50, 25]) {
    if (hull >= threshold) {
      playedHullWarnings.delete(threshold);
    }
  }

  // Only play the lowest applicable threshold
  if (lowestThreshold !== null && !playedHullWarnings.has(lowestThreshold)) {
    playedHullWarnings.add(lowestThreshold);
    const audio = new Audio(`/media/sounds/voice/hull_${lowestThreshold}.mp3`);
    applyEffectsVolume(audio, 0.7);
    void audio.play().catch(() => { /* ignore: may require user gesture */ });
  }
};

/**
 * Submarine-local position used as the hull-leak bubble source.
 * X = along hull axis (positive toward bow), Y = vertical, Z = port/starboard.
 * This is a fixed artistic stand-in; real breach coordinates would come from
 * the server once positional damage data is exposed in the snapshot.
 */
/**
 * Drive the three hull-bubble effects from snapshot state transitions.
 * Must be called after hostView is initialised and after lastSnapshot is set.
 */
let hostLeakOverlay: HullLeakOverlay | null = null;

const applyBubbleEffects = (
  snapshot: GameSnapshot,
  prev: GameSnapshot | null,
  view: HostCockpitView | null
): void => {
  if (!view) return;

  // ── 1. Dock / undock burst ────────────────────────────────────────────────
  if (prev?.submarine.docked === true && snapshot.submarine.docked === false) {
    view.triggerUndockBurst();
  }
  if (prev?.submarine.docked === false && snapshot.submarine.docked === true) {
    view.triggerDockBurst();
  }

  // ── 2. Ballast venting ────────────────────────────────────────────────────
  const ballastMoving = hostBallastTracker.update(Date.now(), snapshot.submarine.frontBallastInput, snapshot.submarine.rearBallastInput);
  view.setBallastVenting(ballastMoving.front, ballastMoving.rear);

  // ── 3. Hull leaks — per zone, only for breached sections ─────────────────
  view.setHullLeaks(snapshot.submarine.hullBreaches);
  if (!hostLeakOverlay && hostStageViewport) {
    hostLeakOverlay = createHullLeakOverlay(hostStageViewport, { sprayFromCracks: true });
  }
  hostLeakOverlay?.update(
    countHullBreaches(snapshot.submarine.hullBreaches),
    false,
    snapshot.submarine.hull,
    undefined,
    leakSprayCount(snapshot.submarine.hullBreaches, snapshot.submarine.hullZones, null)
  );
};

// ─── Host compass overlay ────────────────────────────────────────────────────
let hostCompassCanvas: HTMLCanvasElement | null = null;

const getOrCreateCompassCanvas = (): HTMLCanvasElement | null => {
  if (hostCompassCanvas) return hostCompassCanvas;
  if (!hostStageViewport) return null;
  const c = document.createElement('canvas');
  c.width = 84;
  c.height = 84;
  c.style.position = 'absolute';
  c.style.top = '12px';
  c.style.right = '12px';
  c.style.pointerEvents = 'none';
  c.style.zIndex = '5';
  c.style.borderRadius = '50%';
  hostStageViewport.style.position = 'relative';
  hostStageViewport.appendChild(c);
  hostCompassCanvas = c;
  return c;
};

const drawHostCompass = (snapshot: GameSnapshot): void => {
  const canvas = getOrCreateCompassCanvas();
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  const cx = W / 2;
  const cy = H / 2;
  const R = cx - 2;
  const PI = Math.PI;

  ctx.clearRect(0, 0, W, H);

  // Background
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, PI * 2);
  ctx.fillStyle = 'rgba(4,16,24,0.82)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,180,220,0.55)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Tick marks
  for (let deg = 0; deg < 360; deg += 30) {
    const rad = (deg * PI) / 180;
    const isCardinal = deg % 90 === 0;
    const inner = R - (isCardinal ? 10 : 6);
    ctx.beginPath();
    ctx.moveTo(cx + Math.sin(rad) * inner, cy - Math.cos(rad) * inner);
    ctx.lineTo(cx + Math.sin(rad) * (R - 2), cy - Math.cos(rad) * (R - 2));
    ctx.strokeStyle = isCardinal ? 'rgba(0,200,240,0.9)' : 'rgba(0,160,200,0.5)';
    ctx.lineWidth = isCardinal ? 1.5 : 1;
    ctx.stroke();
  }

  // Cardinal labels
  const cardinals = ['N', 'E', 'S', 'W'];
  ctx.font = 'bold 8px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  [[0, 0, -1], [1, 1, 0], [2, 0, 1], [3, -1, 0]].forEach(([i, dx, dz]) => {
    const lr = R - 18;
    ctx.fillStyle = i === 0 ? '#00e8ff' : 'rgba(0,180,220,0.75)';
    ctx.fillText(cardinals[i], cx + dx * lr, cy + dz * lr);
  });

  // Heading arrow (teal, points in yaw direction)
  const headRad = ((snapshot.submarine.yaw - 90) * PI) / 180;
  const arrowR = R - 22;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + Math.cos(headRad) * arrowR, cy + Math.sin(headRad) * arrowR);
  ctx.strokeStyle = '#00e0c8';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx + Math.cos(headRad) * arrowR, cy + Math.sin(headRad) * arrowR, 3, 0, PI * 2);
  ctx.fillStyle = '#00e0c8';
  ctx.fill();

  // Centre dot
  ctx.beginPath();
  ctx.arc(cx, cy, 3, 0, PI * 2);
  ctx.fillStyle = 'rgba(0,220,200,0.8)';
  ctx.fill();

  // Waypoint diamond (red, if set)
  const wp = snapshot.navWaypoint;
  if (wp) {
    const dX = wp.simulationX - snapshot.submarine.simulationX;
    const dZ = wp.simulationZ - snapshot.submarine.simulationZ;
    const bearRad = Math.atan2(dX, dZ) - PI / 2;
    const wpR = R - 10;
    const wx = cx + Math.cos(bearRad) * wpR;
    const wy = cy + Math.sin(bearRad) * wpR;

    // Pulse
    const pulse = 0.7 + 0.3 * Math.sin(Date.now() / 400);
    const s = 5 * pulse;

    ctx.save();
    ctx.translate(wx, wy);
    ctx.rotate(bearRad + PI / 4);
    ctx.beginPath();
    ctx.moveTo(0, -s);
    ctx.lineTo(s, 0);
    ctx.lineTo(0, s);
    ctx.lineTo(-s, 0);
    ctx.closePath();
    ctx.fillStyle = `rgba(220,60,60,${0.7 + 0.3 * pulse})`;
    ctx.fill();
    ctx.strokeStyle = '#ffcccc';
    ctx.lineWidth = 0.8;
    ctx.stroke();
    ctx.restore();
  }
};
// ─────────────────────────────────────────────────────────────────────────────

const renderSnapshot = (snapshot: GameSnapshot): void => {
  const prevSnapshot = lastSnapshot; // save before overwrite for edge-detection
  lastSnapshot = snapshot;
  // Best-effort admin preview mirror: a quota/serialisation error here must not abort the render.
  try {
    window.localStorage.setItem(ADMIN_PREVIEW_SNAPSHOT_STORAGE_KEY, stringifyAdminPreviewSnapshot(snapshot));
  } catch { /* ignore */ }
  if (!hostView && !hostViewInitFailed) {
    hostView = createHostView();
    hostView?.syncViewportSize();
  }
  hostView?.setSnapshot(snapshot);
  applyBubbleEffects(snapshot, prevSnapshot, hostView);
  if (hullValue) hullValue.textContent = snapshot.submarine.hull.toFixed(0);
  playHullWarning(snapshot.submarine.hull);
  if (powerValue) powerValue.textContent = snapshot.submarine.batteryCharge.toFixed(0) + '%';
  if (fuelValue) fuelValue.textContent = snapshot.submarine.fuel.toFixed(0) + '%';
  if (oxygenValue) oxygenValue.textContent = snapshot.submarine.oxygen.toFixed(0);
  if (outsideTemperatureValue) outsideTemperatureValue.textContent = `${snapshot.submarine.outsideTemperature.toFixed(0)}°C`;
  if (insideTemperatureValue) insideTemperatureValue.textContent = `${snapshot.submarine.insideTemperature.toFixed(0)}°C`;
  if (temperatureRateValue) temperatureRateValue.textContent = `${snapshot.submarine.temperatureRate.toFixed(1)}°C/s`;
  const depthOffset = getHostDepthDisplayOffset();
  if (depthValue) depthValue.textContent = (snapshot.submarine.depth + depthOffset).toFixed(0);
  if (positionValue) positionValue.textContent = `(${snapshot.submarine.simulationX.toFixed(0)}, ${snapshot.submarine.depth.toFixed(0)}, ${snapshot.submarine.simulationZ.toFixed(0)})`;
  if (forwardVelocityValue) forwardVelocityValue.textContent = snapshot.submarine.speed.toFixed(2);
  if (horizontalVelocityValue) horizontalVelocityValue.textContent = snapshot.submarine.lateralSpeed.toFixed(2);
  if (verticalVelocityValue) verticalVelocityValue.textContent = snapshot.submarine.verticalVelocity.toFixed(2);
  alertValue!.textContent = snapshot.submarine.alert ?? 'Nominal';
  if (hostOverloadBanner) {
    const overloadText = snapshot.submarine.engineOverloaded
      ? (snapshot.submarine.powerFailure
          ? 'POWER FAILURE — System shutdown. Restart from engine room.'
          : (snapshot.submarine.alert ?? 'BATTERY BLACKOUT'))
      : '';
    hostOverloadBanner.textContent = overloadText;
    hostOverloadBanner.classList.toggle('visible', overloadText.length > 0);
  }

  const empEventTick = snapshot.submarine.empEventTick ?? null;
  if (typeof empEventTick === 'number' && empEventTick !== lastHostEmpEventTick) {
    lastHostEmpEventTick = empEventTick;
    triggerHostEmpEffects();
  }

  const summary = snapshot.gameSummary;
  if (summary) {
    if (hostEndGameSummary) {
      hostEndGameSummary.hidden = false;
    }
    if (hostEndGameReason) {
      hostEndGameReason.textContent = summary.reason;
    }
    if (hostEndGameDuration) {
      hostEndGameDuration.textContent = formatDuration(summary.durationMs);
    }
    if (hostEndGameDistance) {
      hostEndGameDistance.textContent = `${summary.distanceTravelledMeters.toFixed(0)} m`;
    }
    if (hostEndGameResources) {
      hostEndGameResources.textContent = `${summary.resourcesCollected}`;
    }
    if (hostEndGameSonar) {
      hostEndGameSonar.textContent = `${summary.sonarPulseCount}`;
    }
    if (hostEndGameChart) {
      renderMetricChart(hostEndGameChart, summary.metricHistory ?? []);
    }
  } else if (hostEndGameSummary) {
    hostEndGameSummary.hidden = true;
  }
  phaseValue!.textContent = snapshot.phase === 'running' ? 'Mission running' : 'Lobby';
  lastInput!.textContent = snapshot.lastInput;

  // Keep the continuous atmospheric loops (engine hum, ballast hiss, terrain
  // scrape, pressure creak) in sync with the latest submarine state.
  updateSubmarineAmbience(snapshot.submarine);

  if (snapshot.lastInput && snapshot.lastInput !== lastLoggedInput) {
    lastLoggedInput = snapshot.lastInput;
    if (
      !snapshot.lastInput.includes(' (pilot)') &&
      !snapshot.lastInput.includes('adjusted ballast flow')
    ) {
      appendHostEvent(`Input: ${snapshot.lastInput}`);
    }
  }

  setSaveGameButtonState();
  drawHostCompass(snapshot);
};

createRoomButton?.addEventListener('click', () => {
  // If a room already exists, open the mission config popup directly.
  // Otherwise create one first (fallback for failed auto-create).
  if (roomCode) {
    openMissionConfig();
  } else {
    createRoomAfterClosingExisting();
  }
});

document.querySelector<HTMLButtonElement>('#entryLoadGameButton')?.addEventListener('click', () => {
  loadGameInput?.click();
});

// Mission config popup open/close
const openMissionConfig = (): void => {
  if (missionConfigPopup) {
    missionConfigPopup.hidden = false;
    document.body.style.overflow = 'hidden';
  }
};

const closeMissionConfig = (): void => {
  if (missionConfigPopup) {
    missionConfigPopup.hidden = true;
    document.body.style.overflow = '';
  }
};

openMissionConfigButton?.addEventListener('click', openMissionConfig);
closeMissionConfigButton?.addEventListener('click', closeMissionConfig);

missionConfigPopup?.addEventListener('click', (event) => {
  if (event.target === missionConfigPopup) {
    closeMissionConfig();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && missionConfigPopup && !missionConfigPopup.hidden) {
    closeMissionConfig();
  }
});

// Copy host URL to clipboard
document.querySelector<HTMLElement>('#hostShareLinkDisplay')?.addEventListener('click', (event) => {
  event.preventDefault();
  void navigator.clipboard.writeText(window.location.href).then(() => {
    const el = event.target as HTMLElement;
    const original = el.textContent;
    el.textContent = 'Copied!';
    setTimeout(() => { el.textContent = original; }, 1800);
  });
});

const restartGame = (): void => {
  if (!roomCode) return;
  socket.emit(EVENTS.restartGame, { code: roomCode });
  appendHostEvent('Host restarted game.');
  if (hostEndGameSummary) hostEndGameSummary.hidden = true;
  if (hostMissionBanner) hostMissionBanner.hidden = true;
};

restartButton?.addEventListener('click', restartGame);
hostEndGameRestartButton?.addEventListener('click', restartGame);

stopGameButton?.addEventListener('click', () => {
  if (!roomCode) return;
  const confirmed = window.confirm('Stop the active game and return to setup?');
  if (!confirmed) return;
  socket.emit(EVENTS.stopGame, { code: roomCode });
  appendHostEvent('Host stopped game and returned to setup.');
});

document.querySelector<HTMLButtonElement>('#entryStopGameButton')?.addEventListener('click', () => {
  if (!roomCode) return;
  const confirmed = window.confirm('Stop the active game and start over?');
  if (!confirmed) return;
  socket.emit(EVENTS.stopGame, { code: roomCode });
  appendHostEvent('Host stopped game from entry screen.');
});

const getSelectedMissionAndDifficulty = (): { mission: string; difficulty: string; campaignId?: string } => {
  const selected = missionSelect?.value || '';
  const difficulty = difficultySelect?.value || 'normal';
  if (selected.startsWith(CAMPAIGN_OPTION_PREFIX)) {
    const campaignId = selected.slice(CAMPAIGN_OPTION_PREFIX.length);
    return { mission: campaignFirstMap.get(campaignId) ?? '', difficulty, campaignId };
  }
  return { mission: selected, difficulty };
};

startButton?.addEventListener('click', () => {
  if (!roomCode) {
    return;
  }

  closeMissionConfig();
  setEntryCreating();
  const { mission, difficulty, campaignId } = getSelectedMissionAndDifficulty();
  socket.emit(EVENTS.setLobbyConfig, { code: roomCode, mission, difficulty, campaignId, startChapter: campaignId ? pendingStartChapter : undefined });
  pendingStartChapter = undefined;
  socket.emit(EVENTS.startGame, { code: roomCode });
  appendHostEvent(`Game started (${campaignId ? `campaign ${campaignId}` : mission}, ${difficulty}).`);
  // Reload render tuning from localStorage after starting a mission
  void loadRenderTuning();
  // Ensure the latest tuning is applied after the host view is created
  setTimeout(() => { hostView?.setRenderTuning(hostRenderTuning); }, 0);
});

missionSelect?.addEventListener('change', () => {
  pendingStartChapter = undefined;
  refreshStartChapterOptions();
  if (!roomCode) return;
  const { mission, difficulty, campaignId } = getSelectedMissionAndDifficulty();
  socket.emit(EVENTS.setLobbyConfig, { code: roomCode, mission, difficulty, campaignId, startChapter: campaignId ? 0 : undefined });
});

difficultySelect?.addEventListener('change', () => {
  if (!roomCode) return;
  const { mission, difficulty, campaignId } = getSelectedMissionAndDifficulty();
  socket.emit(EVENTS.setLobbyConfig, { code: roomCode, mission, difficulty, campaignId });
});

graphicsQualitySelect?.addEventListener('change', () => {
  setGraphicsQuality(graphicsQualitySelect.value);
  appendHostEvent(`Graphics quality changed to ${hostGraphicsQuality}.`);
});

runtimeGraphicsQualitySelect?.addEventListener('change', () => {
  setGraphicsQuality(runtimeGraphicsQualitySelect.value);
  appendHostEvent(`Graphics quality changed to ${hostGraphicsQuality}.`);
});

const closeRoomWithConfirmation = (): void => {
  if (!roomCode) return;
  const confirmed = window.confirm(
    'Close this game room for everyone and return to the first host screen?'
  );
  if (!confirmed) return;
  socket.emit(EVENTS.closeRoom, { code: roomCode });
};

closeGameButton?.addEventListener('click', closeRoomWithConfirmation);
closeRoomButton?.addEventListener('click', closeRoomWithConfirmation);

socket.on('connect', () => {
  if (hostStatusElement && !hostAutoRestorePending) {
    hostStatusElement.textContent = 'Connected. Create a room to begin.';
  }
  attemptHostRestore();
});

socket.on('connect_error', (error: Error) => {
  console.error('Host socket connection error:', error);
  if (hostStatusElement) {
    hostStatusElement.textContent = `Connection failed: ${error.message}`;
  }
});

socket.on('disconnect', (reason: string) => {
  if (hostStatusElement) {
    hostStatusElement.textContent = `Disconnected (${reason}). Reconnecting...`;
  }
});

setupRenderTuningSync(socket, applyRenderTuning, RENDER_TUNING_STORAGE_KEY, {
  onSocketUpdate: () => appendHostEvent('Render tuning updated from admin controls.'),
  onStorageUpdate: () => appendHostEvent('Render tuning updated from admin controls.')
});

window.addEventListener('error', (event) => {
  console.error('Host runtime error:', event.error ?? event.message);
  appendHostEvent(`Runtime error: ${event.message}`);
  if (hostStatusElement) {
    hostStatusElement.textContent = `Runtime error in host view: ${event.message}`;
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason instanceof Error ? event.reason.message : String(event.reason ?? 'Unknown error');
  console.error('Host unhandled rejection:', event.reason);
  appendHostEvent(`Unhandled error: ${reason}`);
  if (hostStatusElement) {
    hostStatusElement.textContent = `Unhandled error in host view: ${reason}`;
  }
});


socket.on(EVENTS.hostSession, (payload: HostSessionPayload) => {
  hostReconnectToken = payload.reconnectToken;
  roomCode = payload.code;
  persistHostSession();
  if (payload.restored || hostAutoRestorePending) {
    showHostBanner(`Recovered host room ${payload.code}.`);
  }
  hostAutoRestorePending = false;
});

socket.on(EVENTS.roomCreated, (state: RoomState) => {
  void renderRoomState(state).then(startPendingUploadedMap);
});

// Story narration on the host page: subtitles always; the clip only when no phone is connected (phones play it).
const hostVoice = createVoiceLines({
  mode: 'cast',
  shouldPlayAudio: () => !(lastRoomState?.players ?? []).some((p) => p.connected),
  onLogUnlocked: () => renderHostLogsRecovered()
});
socket.on(EVENTS.voiceLine, (line: import('../shared/narrative.js').VoiceLinePayload) => hostVoice.speak(line));
socket.on(EVENTS.logUnlocked, (payload: import('../shared/narrative.js').LogUnlockedPayload) => hostVoice.logUnlocked(payload));

/** Compact "Logs recovered" list under the lobby roster (visible between chapters and in the lobby). */
const renderHostLogsRecovered = (): void => {
  if (!lobbyPlayersElement?.parentElement) return;
  let box = document.querySelector<HTMLElement>('#hostLogsRecovered');
  const logs = getRecoveredLogs(lastRoomState);
  if (!lastRoomState?.campaign || logs.length === 0) {
    if (box) box.hidden = true;
    return;
  }
  if (!box) {
    box = document.createElement('div');
    box.id = 'hostLogsRecovered';
    box.style.cssText = 'margin-top:0.8rem;font-size:0.8rem;';
    lobbyPlayersElement.parentElement.append(box);
  }
  box.hidden = false;
  box.replaceChildren();
  const heading = document.createElement('div');
  heading.style.cssText = 'font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#e8b84b;margin-bottom:0.25rem;';
  heading.textContent = `Logs recovered (${logs.length})`;
  box.append(heading);
  for (const log of logs) {
    const row = document.createElement('div');
    row.textContent = log.title;
    row.title = log.body;
    box.append(row);
  }
};
onLogsChanged(renderHostLogsRecovered);
void loadLogCatalog();

socket.on(EVENTS.roomState, (state: RoomState) => {
  void renderRoomState(state).then(startPendingUploadedMap);
});

socket.on(EVENTS.customMapReloaded, () => {
  if (lastRoomState) {
    void renderRoomState(lastRoomState, true);
  }
});

socket.on(EVENTS.gameStarted, (state: RoomState) => {
  playedHullWarnings.clear();
  void renderRoomState(state).then(() => {
    if (missionBannerTimeout !== null) {
      clearTimeout(missionBannerTimeout);
      missionBannerTimeout = null;
    }
    if (hostMissionBanner) hostMissionBanner.hidden = true;
  });
});

const hostSnapshotAssembler = new SnapshotAssembler(() => {
  if (roomCode) socket.emit(EVENTS.requestFullSnapshot, { code: roomCode });
});
const handleHostSnapshot = (snapshot: GameSnapshot): void => {
  if (snapshot.world.resourceNodes) {
    cachedResourceNodes = snapshot.world.resourceNodes;
  } else {
    snapshot.world.resourceNodes = cachedResourceNodes;
  }
  renderSnapshot(snapshot);
};
socket.on(EVENTS.stateSnapshot, (snapshot: GameSnapshot) => handleHostSnapshot(hostSnapshotAssembler.full(snapshot)));
socket.on(EVENTS.stateSnapshotDelta, (delta: SnapshotDelta) => {
  const rebuilt = hostSnapshotAssembler.delta(delta);
  if (rebuilt) handleHostSnapshot(rebuilt);
});
socket.on('connect', () => hostSnapshotAssembler.reset());
installVisibilityReporter(socket, EVENTS.clientVisibility);

socket.on(EVENTS.sonarPulse, (_payload: SonarPulsePayload) => {
  triggerSonarPulseEffects();
});

socket.on(EVENTS.weaponsImpact, (payload: WeaponsImpactPayload) => {
  hostView?.triggerWeaponsImpact(payload.simulationX, payload.simulationZ, payload.depth, payload.hit);
});

socket.on(EVENTS.collisionImpact, (payload: CollisionImpactPayload) => {
  hostView?.triggerCollisionShake(payload.strength);
  if (payload.collisionLocation) {
    hostView?.triggerCollisionCloud(payload.strength, payload.collisionLocation.x, payload.collisionLocation.z);
  }
  playHostCollisionImpactSound(payload.strength);
  // Feed the sustained scrape loop so continuous slides along terrain keep ringing.
  // Worm strikes are creature attacks, not terrain grinding, so exclude them.
  if (payload.source !== 'worm') {
    notifyTerrainContact(payload.strength);
  }
});

// Unlock the atmospheric audio context on the first host interaction.
initializeSubmarineAmbience();

socket.on(EVENTS.powerFailure, (_payload: PowerFailurePayload) => {
  playHostPowerDownSound();
  appendHostEvent('POWER FAILURE — battery depleted. All systems offline.');
});

socket.on(EVENTS.hostEventLogEntry, (payload: HostEventLogEntryPayload) => {
  appendHostEvent(payload.message);
});

socket.on(EVENTS.fuelHarvested, (payload: { at: number; amount: number }) => {
  appendHostEvent(`Fuel harvested: +${payload.amount}% from rock salvage.`);
});

socket.on(EVENTS.errorMessage, (payload: { message: string }) => {
  if (hostAutoRestorePending) {
    hostAutoRestorePending = false;
    hostReconnectToken = '';
    clearHostSession();
  }
  if (hostStatusElement) {
    hostStatusElement.textContent = payload.message;
  }
});

socket.on(EVENTS.roomClosed, () => {
  roomCode = '';
  hostReconnectToken = '';
  lastRoomState = null;
  lastSnapshot = null;
  lastLoggedInput = '';
  window.localStorage.removeItem(ADMIN_PREVIEW_ROOM_STATE_STORAGE_KEY);
  window.localStorage.removeItem(ADMIN_PREVIEW_SNAPSHOT_STORAGE_KEY);
  clearHostSession();
  if (playersElement) playersElement.innerHTML = '';
  if (lobbyPlayersElement) lobbyPlayersElement.innerHTML = '';
  if (hostEventLog) hostEventLog.innerHTML = '';
  if (roomCodeElement) roomCodeElement.textContent = '----';
  if (hostMissionSelectControl) hostMissionSelectControl.hidden = false;
  if (phaseValue) phaseValue.textContent = 'Lobby';
  closeMissionConfig();
  setHostScreen('entry');
  updateJoinLinkAndQr();
  if (hostStatusElement) hostStatusElement.textContent = 'Room closed. Creating a new game…';

  if (createFreshRoomPending) {
    createFreshRoomPending = false;
    createFreshRoom();
  } else {
    // Auto-create a new room after closing so lobby is immediately ready
    createFreshRoom();
  }
});

setHostScreen('entry');
setSaveGameButtonState();
updateJoinLinkAndQr();
void loadRenderTuning();
void loadCustomMissionOptions();
